import { type FC } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export type CapacityCardStatus = "default" | "success" | "warning" | "error";

export interface CapacityCardProps {
    /** Uppercase eyebrow label (e.g. "ROVO CREDITS") */
    label: string;
    /** Current/estimated value */
    value: number;
    /** Plan limit — when provided, shows "value / maxValue" format */
    maxValue?: number;
    /** Descriptive text below the value */
    description?: string;
    /** Visual status state */
    status?: CapacityCardStatus;
    /** Additional CSS class name */
    className?: string;
}

const cardStyles = css({
    backgroundColor: wacToken("color.neutral.light0"),
    border: `1px solid ${wacToken("color.neutral.light300")}`,
    borderRadius: "12px",
    padding: `${token("space.200")} ${token("space.250")}`,
    width: "100%",
    boxSizing: "border-box"
});

const headerStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const labelStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: wacToken("color.neutral.light700")
});

const valueContainerStyles = css({
    marginBlockStart: token("space.050")
});

const valueStatusStyles = {
    default: css({
        color: wacToken("color.primary.black")
    }),
    success: css({
        color: wacToken("color.primary.black")
    }),
    warning: css({
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#974F0C"
    }),
    error: css({
        color: wacToken("color.secondary.red")
    })
};

const valueStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "28px",
    lineHeight: "36px"
});

const maxValueStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 400,
    fontSize: "28px",
    lineHeight: "36px",
    color: wacToken("color.neutral.light700")
});

const descriptionStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    lineHeight: "20px",
    color: wacToken("color.neutral.light700"),
    marginBlockStart: token("space.100"),
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
});

const formatNumber = (num: number): string => num.toLocaleString("en-US");

export const CapacityCard: FC<CapacityCardProps> = ({
    label,
    value,
    maxValue,
    description,
    status = "default",
    className
}) => {
    const showComparison = maxValue !== undefined && maxValue !== null;
    const isOverCapacity = showComparison && value > (maxValue as number);
    const effectiveStatus = isOverCapacity
        ? status === "default"
            ? "error"
            : status
        : status;

    const descriptionId = `capacity-card-desc-${label
        .replace(/\s+/g, "-")
        .toLowerCase()}`;

    return (
        <div
            css={cardStyles}
            className={className}
            data-testid={`capacity-card-${label
                .replace(/\s+/g, "-")
                .toLowerCase()}`}
        >
            <div css={headerStyles}>
                <span css={labelStyles}>{label}</span>
            </div>

            <div css={valueContainerStyles}>
                <span css={[valueStyles, valueStatusStyles[effectiveStatus]]}>
                    {formatNumber(value)}
                </span>
                {showComparison && (
                    <span css={maxValueStyles}>
                        {" "}
                        / {formatNumber(maxValue as number)}
                    </span>
                )}
            </div>

            {description && (
                <p css={descriptionStyles} id={descriptionId}>
                    {description}
                </p>
            )}
        </div>
    );
};
